import { CircleUser } from "lucide-react";
import { PropsWithChildren } from "react";
import { EnabledPage, FrontendPageEnum } from "shared/model/pages";
import defaultTheme from "tailwindcss/defaultTheme";

import { ButtonProfile } from "./button-profile";
import { ButtonSignOut } from "./button-signout";
import { ButtonTutorial } from "./button-tutorial";
import { InjectTypeForm } from "../../components/inject-typeform";
import { LockGuard } from "../../components/lock";
import { UserFeedbackModal } from "../../components/user-feedback/user-feedback-modal";
import { Drawer } from "~/components/app-shell/drawer/drawer";
import { LogoWide } from "~/components/app-shell/logo";
import { cn } from "~/util/cn";
import { useKeyboardStatus, useWindowDimensions } from "~/util/hooks";

export const TenantPage: React.FC<
  PropsWithChildren<{ page?: EnabledPage }>
> = ({ children, page }) => {
  return (
    <Drawer>
      <UserFeedbackModal />
      <InjectTypeForm page={page}>
        <MaybeSummary page={page}>{children}</MaybeSummary>
      </InjectTypeForm>
    </Drawer>
  );
};

const getLgBreakpoint = (): number => {
  try {
    return Number(defaultTheme.screens.lg.match(/(\d+)px/)?.at(1));
  } catch {
    return 1020;
  }
};

const MaybeSummary: React.FC<
  React.PropsWithChildren<{ page?: EnabledPage }>
> = ({ page, children }) => {
  const { width } = useWindowDimensions();
  const isSummaryPage = page?.route === FrontendPageEnum.REPORT;
  const showHeader = width < getLgBreakpoint() && isSummaryPage;

  const { isKeyboardOpen } = useKeyboardStatus();

  return (
    <div>
      {showHeader ? (
        <header className="flex h-[4.5rem] flex-nowrap items-center justify-between gap-2 py-3 pl-6 pr-4">
          <LogoWide />

          <details className="dropdown-end dropdown">
            <summary className="btn-ghost btn-square btn text-neutral-700">
              <CircleUser />
            </summary>
            <ul
              tabIndex={0}
              className="shadow-btn dropdown-content menu z-[1] w-52 rounded-box border border-neutral-200 bg-base-100 p-2"
            >
              <li>
                <ButtonProfile className="text-neutral btn-ghost btn-block btn justify-start font-medium" />
              </li>
              <li className="mt-2">
                <ButtonTutorial className="text-neutral btn-ghost btn-block btn justify-start font-medium" />
              </li>
              <li className="mt-2">
                <ButtonSignOut className="text-neutral btn-ghost btn-block btn justify-start font-medium" />
              </li>
            </ul>
          </details>
        </header>
      ) : null}

      <div
        className={cn(
          "lg:h-dvh",
          isSummaryPage
            ? "h-[calc(100dvh-4.25rem-4.40rem)]"
            : isKeyboardOpen
            ? "h-dvh"
            : "h-[calc(100dvh-4.25rem)]",
        )}
      >
        <LockGuard page={page}>{children}</LockGuard>
      </div>
    </div>
  );
};
